// Synthesizes FACU sounds via Web Audio API — no external audio files, no copyright concerns.
// Generates code-aligned signals:
//   ALARM  → NFPA Temporal-3 pattern (0.5s ON · 0.5s OFF · ×3 · 1.5s OFF) at ~3100 Hz
//   TROUBLE → Slow single chirp every 4 sec at 2200 Hz
//   SUPERVISORY → Two-beep pattern every 4 sec at 1800 Hz
//   PIEZO → Continuous fast chirp at 3500 Hz (panel buzzer)
//   DRILL → Same Temporal-3 alarm pattern (drills sound the same as alarms — NFPA 72 §18.4.2)
//   CLICK → Short ~30ms blip on button press
//
// Browser autoplay policy: AudioContext is created lazily on the first user gesture.

import { useRef, useCallback, useEffect } from "react";

export function useFacuAudio({ muted }) {
  const ctxRef = useRef(null);
  const nodesRef = useRef({}); // active oscillator/gain nodes by sound id
  const masterRef = useRef(null);

  const ensureCtx = useCallback(() => {
    if (typeof window === "undefined") return null;
    if (!ctxRef.current) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        const ctx = new Ctx();
        const master = ctx.createGain();
        master.gain.value = muted ? 0 : 0.18; // master headroom — NEVER set to full
        master.connect(ctx.destination);
        ctxRef.current = ctx;
        masterRef.current = master;
      } catch {
        return null;
      }
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume().catch(() => {});
    return ctxRef.current;
  }, [muted]);

  // Mirror mute changes onto master gain
  useEffect(() => {
    if (masterRef.current) {
      const target = muted ? 0 : 0.18;
      try { masterRef.current.gain.setTargetAtTime(target, ctxRef.current.currentTime, 0.05); }
      catch { /* gain-node teardown race; non-fatal */ }
    }
  }, [muted]);

  // ─── stop helpers ─────────────────────────────────────────────
  const stopId = useCallback((id) => {
    const node = nodesRef.current[id];
    if (!node) return;
    try {
      if (node.intervalId) clearInterval(node.intervalId);
      if (node.timeoutId) clearTimeout(node.timeoutId);
      if (node.osc) {
        node.osc.stop();
        node.osc.disconnect();
      }
      if (node.gain) node.gain.disconnect();
    } catch { /* osc already stopped; harmless */ }
    delete nodesRef.current[id];
  }, []);

  const stopAll = useCallback(() => {
    Object.keys(nodesRef.current).forEach((id) => stopId(id));
  }, [stopId]);

  // ─── tone primitive ───────────────────────────────────────────
  // Single short tone — used as a building block.
  const blip = useCallback((freq, durationMs, gainPeak = 0.6, type = "square") => {
    const ctx = ensureCtx();
    if (!ctx || !masterRef.current) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gainPeak, t + 0.005);
    g.gain.linearRampToValueAtTime(gainPeak * 0.85, t + (durationMs / 1000) * 0.7);
    g.gain.linearRampToValueAtTime(0, t + durationMs / 1000);
    osc.connect(g).connect(masterRef.current);
    osc.start(t);
    osc.stop(t + durationMs / 1000 + 0.01);
  }, [ensureCtx]);

  // ─── alarm — NFPA Temporal-3 ──────────────────────────────────
  const startAlarm = useCallback(() => {
    if (nodesRef.current.alarm) return;
    const cycleMs = 4000; // 0.5×3 ON + 0.5×2 OFF + 1.5 trailing
    const tick = () => {
      // Three 0.5 s pulses with 0.5 s gaps
      blip(3100, 500, 0.5, "square");
      const t1 = setTimeout(() => blip(3100, 500, 0.5, "square"), 1000);
      const t2 = setTimeout(() => blip(3100, 500, 0.5, "square"), 2000);
      // Track timeouts so we can clear if alarm stops mid-cycle
      nodesRef.current.alarm.pending = [t1, t2];
    };
    const intervalId = setInterval(tick, cycleMs);
    nodesRef.current.alarm = { intervalId, pending: [] };
    tick();
  }, [blip]);

  const stopAlarm = useCallback(() => {
    const node = nodesRef.current.alarm;
    if (!node) return;
    clearInterval(node.intervalId);
    (node.pending || []).forEach((t) => clearTimeout(t));
    delete nodesRef.current.alarm;
  }, []);

  // ─── trouble — slow chirp every 4 sec ─────────────────────────
  const startTrouble = useCallback(() => {
    if (nodesRef.current.trouble) return;
    const tick = () => blip(2200, 180, 0.35, "triangle");
    tick();
    const intervalId = setInterval(tick, 4000);
    nodesRef.current.trouble = { intervalId };
  }, [blip]);

  const stopTrouble = useCallback(() => stopId("trouble"), [stopId]);

  // ─── supervisory — two-beep pattern every 4 sec ───────────────
  const startSupervisory = useCallback(() => {
    if (nodesRef.current.supervisory) return;
    const tick = () => {
      blip(1800, 150, 0.4, "sine");
      const t1 = setTimeout(() => blip(1800, 150, 0.4, "sine"), 250);
      nodesRef.current.supervisory.pending = [t1];
    };
    const intervalId = setInterval(tick, 4000);
    nodesRef.current.supervisory = { intervalId, pending: [] };
    tick();
  }, [blip]);

  const stopSupervisory = useCallback(() => {
    const node = nodesRef.current.supervisory;
    if (!node) return;
    clearInterval(node.intervalId);
    (node.pending || []).forEach((t) => clearTimeout(t));
    delete nodesRef.current.supervisory;
  }, []);

  // ─── piezo — continuous panel buzzer chirp ────────────────────
  const startPiezo = useCallback(() => {
    if (nodesRef.current.piezo) return;
    const tick = () => blip(3500, 90, 0.25, "square");
    tick();
    const intervalId = setInterval(tick, 200);
    nodesRef.current.piezo = { intervalId };
  }, [blip]);

  const stopPiezo = useCallback(() => stopId("piezo"), [stopId]);

  // ─── click — short button press ───────────────────────────────
  const click = useCallback(() => {
    blip(800, 30, 0.4, "square");
  }, [blip]);

  // Cleanup on unmount
  useEffect(() => () => stopAll(), [stopAll]);

  return {
    startAlarm, stopAlarm,
    startTrouble, stopTrouble,
    startSupervisory, stopSupervisory,
    startPiezo, stopPiezo,
    click,
    stopAll,
    ensureCtx,
  };
}
